import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { apiFetch } from "./utils/api";
import { formatDate } from "./utils/time";
import { useAuth } from "./context/AuthContext";
import Header from "./components/Header";
import BackButton from "./components/BackButton";
import Footer from "./components/Footer";
import { SkeletonArticle } from "./components/ui";
import NotAvailable from "./components/NotAvailable";
import "./Content.css";

/**
 * The full policy. Like the update detail page, the audience is re-checked
 * server-side: a deep link to /policy/2 returns 404 unless this viewer is in its
 * audience, whether or not the card ever appeared in their list.
 */
const PolicyDetails = () => {
    const { id } = useParams();
    const { isAuthenticated } = useAuth();

    const [policy, setPolicy] = useState(null);
    const [status, setStatus] = useState("loading"); // loading | ready | notfound

    useEffect(() => {
        let cancelled = false;

        (async () => {
            setStatus("loading");
            try {
                const res = await apiFetch(`/api/Dolluzcorp/policies/${id}`);
                const data = await res.json();
                if (cancelled) return;

                if (res.ok && data.success) {
                    setPolicy(data.data);
                    setStatus("ready");
                } else {
                    setStatus("notfound");
                }
            } catch {
                if (!cancelled) setStatus("notfound");
            }
        })();

        return () => { cancelled = true; };
    }, [id, isAuthenticated]);

    return (
        <>
            <a className="skip-link" href="#main">Skip to content</a>
            <Header />

            <main id="main" className="article">
                <div className="page-wrap">
                    <BackButton fallback="/policies" />

                    {status === "loading" && <SkeletonArticle />}

                    {status === "notfound" && (
                        <NotAvailable kind="policy" backTo="/policies" backLabel="Browse policies" />
                    )}

                    {status === "ready" && policy && (
                        <article>
                            <header className="article-head">
                                <span className="chip chip-policy">Policy</span>
                                <h1 className="article-title">{policy.title}</h1>
                                {policy.excerpt && <p className="article-standfirst">{policy.excerpt}</p>}
                                <div className="article-meta">
                                    <span>Last updated</span>
                                    <time dateTime={policy.updated_time || policy.created_time}>
                                        {formatDate(policy.updated_time || policy.created_time)}
                                    </time>
                                </div>
                            </header>

                            <div
                                className="prose article-body"
                                dangerouslySetInnerHTML={{ __html: policy.description }}
                            />
                        </article>
                    )}
                </div>
            </main>

            <Footer />
        </>
    );
};

export default PolicyDetails;
